import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import MemberTable from '../../../components/tables/MemberTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useMemberApi } from '../../../hooks/useMemberApi';
import AddNewButton from '../../../components/common/AddButton';
import { AuthDTO, ImpersonationDTO, MemberDTO } from '../../../services/apiClient';
import { useNavigate, useLocation } from 'react-router-dom';
import { getUserId } from '../../../utils/tokenUtils';
import { useAuthApi } from '../../../hooks/useAuthApi';

const MemberList: React.FC = () => {
  const {
    search,
    remove,
    isLoading,
  } = useMemberApi();
  const { impersonateMemberLogin } = useAuthApi();
  const [items, setItems] = useState<MemberDTO[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchFilters, setSearchFilters] = useState<any>({});

  const [showAdvanced, setShowAdvanced] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Get status from URL and map it correctly
  const statusFromUrl = location.pathname.includes('/status/')
    ? location.pathname.split('/status/')[1].toLowerCase()
    : '';

  // Add status mapping state
  const [statusMap, setStatusMap] = useState<{ [key: string]: string }>({});

  // Function to get the correct filters based on status
  const getFiltersFromStatus = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return {
          memberStatusId: statusMap['active'],
          isDeleted: false,
          isArchived: false,
        };
      case 'none':
        return {
          memberStatusId: statusMap['none'],
          isDeleted: false,
          isArchived: false,
        };
      case 'archived':
        return { isArchived: true };
      case 'deleted':
        return { isDeleted: true };
      default:
        return {};
    }
  };

  // Function to get display name for status
  const getStatusDisplayName = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'Active';
      case 'none':
        return 'No Status';
      case 'archived':
        return 'Archived';
      case 'deleted':
        return 'Deleted';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const fetchItems = async (page: number, size: number, filters: any) => {
    try {
      const statusFilters = statusFromUrl
        ? getFiltersFromStatus(statusFromUrl)
        : {};
      const response = await search({
        filters: {
          ...filters,
          ...statusFilters,
        },
        page,
        pageSize: size,
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });
      setItems(response?.data?.results as MemberDTO[] || []);
      setTotalRecords(response?.data?.totalCount || 0);
    } catch (error) {
      console.error('Error fetching Members:', error);
    }
  };

  // Replace the useEffect that watches for page changes
  useEffect(() => {
    fetchItems(currentPage, pageSize, {
      ...searchFilters,
      ...getFiltersFromStatus(statusFromUrl),
    });
  }, [currentPage, pageSize, statusFromUrl, statusMap]);

  // Update the handleSearch function
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(1, pageSize, {
      ...searchFilters,
      ...getFiltersFromStatus(statusFromUrl),
    });
  };

  const handleClearSearch = () => {
    setSearchFilters({});
    fetchItems(1, pageSize, {});
  };

  // Add state for dropdown options
  const [memberTypeOptions, setMemberTypeOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [memberCategoryOptions, setMemberCategoryOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [memberIdTypeOptions, setMemberIdTypeOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [languageOptions, setLanguageOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [countryOptions, setCountryOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [provinceOptions, setProvinceOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [memberStatusOptions, setMemberStatusOptions] = useState<
    { value: string; label: React.ReactNode }[]
  >([]);
  const [disciplinaryStatusOptions, setDisciplinaryStatusOptions] = useState<
    { value: string; label: React.ReactNode }[]
  >([]);
  const [terminationStatusOptions, setTerminationStatusOptions] = useState<
    { value: string; label: React.ReactNode }[]
  >([]);
  const [registrationTypeOptions, setRegistrationTypeOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [registrationStepOptions, setRegistrationStepOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [referralTypeOptions, setReferralTypeOptions] = useState<
    { value: string; label: string }[]
  >([]);

  // Add boolean options
  const booleanOptions = [
    { value: 'true', label: 'Yes' },
    { value: 'false', label: 'No' },
  ];

  const handleDelete = async (member: MemberDTO) => {
    try {
      await remove(member.id ?? '');
      await fetchItems(currentPage, pageSize, searchFilters);
    } catch (error) {
      console.error('Error deleting member:', error);
    }
  };

  const handleImpersonateLogin = async (dto: MemberDTO): Promise<void> => {
    const impersonateDto: ImpersonationDTO = {
      memberId: dto.userID,
      adminId: getUserId() ?? '',
    };

    try {
      const response = await impersonateMemberLogin(impersonateDto);

      if (response && response.success && response.data) {
        const responseData = response.data as AuthDTO;

        localStorage.setItem('token', responseData.accessToken ?? '');
        localStorage.setItem('refresh_token', responseData.refreshToken ?? '');
        navigate('/admin/dashboard');
      }
    } catch (error) {
      console.error('Error impersonating login as member', error);
    }
  };

  // Update the form fields to use dropdowns
  return (
    <AdminLayout
      activeMenuItem={
        statusFromUrl
          ? `/admin/members/status/${statusFromUrl}`
          : '/admin/members'
      }
      breadcrumbs={[
        { label: 'Admin', href: '#' },
        { label: 'Members', href: '#' },
        ...(statusFromUrl
          ? [
              {
                label: getStatusDisplayName(statusFromUrl),
                href: '#',
              },
            ]
          : []),
      ]}
      allowedRoles={['Admin']}
      requiredPermission="Manage.Members.View"
    >
      <Card
        title={`Manage Members ${statusFromUrl ? `- ${getStatusDisplayName(statusFromUrl)}` : ''}`}
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <div className="flex space-x-2">
            <Button
              variant="secondary"
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              {showAdvanced ? 'Basic Search' : 'Advanced Search'}
            </Button>
            <AddNewButton onClick={() => navigate('/admin/members/create')} />
          </div>
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          {/* Basic Search Fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <FormField
              id="firstName"
              label="Search by First Name"
              value={searchFilters.firstName || ''}
              onChange={(e) =>
                setSearchFilters({
                  ...searchFilters,
                  firstName: e.target.value,
                })
              }
              placeholder="Search by First Name"
            />
            <FormField
              id="lastName"
              label="Search by Last Name"
              value={searchFilters.lastName || ''}
              onChange={(e) =>
                setSearchFilters({
                  ...searchFilters,
                  lastName: e.target.value,
                })
              }
              placeholder="Search by Last Name"
            />
          </div>

          <div className="flex space-x-4 mt-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <MemberTable
          data={items}
          handleEdit={(dto) => navigate(`/admin/members/${dto.id}`)}
          handleDelete={handleDelete}
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / pageSize)}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading} 
          handleImpersonateLogin={handleImpersonateLogin}      />
      </Card>
    </AdminLayout>
  );
};

export default MemberList;
